import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Card from "../components/Card";
import RedButton from "../components/RedButton";
import BlueButton from "../components/BlueButton";
import EntitySearchSelect from "../components/EntitySearchSelect";

export default function PullBackFlow() {
  const [fromType, setFromType] = useState("");
  const [fromId, setFromId] = useState("");
  const [packageList, setPackageList] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");

  const [startKey, setStartKey] = useState("");
  const [reason, setReason] = useState("");
  const [destRole, setDestRole] = useState("");
  const [destId, setDestId] = useState("");

  const [allowedFromRoles, setAllowedFromRoles] = useState([]);
  const [allowedDestRoles, setAllowedDestRoles] = useState([]);

  const [fromEntities, setFromEntities] = useState([]);
  const [destEntities, setDestEntities] = useState([]);

  const [loadingFrom, setLoadingFrom] = useState(false);
  const [loadingDest, setLoadingDest] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);

  /* -------------------------------------------------
     🔐 SESSION USER (SAFE)
  ------------------------------------------------- */
  const authUserString = sessionStorage.getItem("authUserData");
  const sessionUser = JSON.parse(authUserString);

  const sessionId = sessionUser.id;
  const sessionRole = Number(sessionUser.role);
  const sessionCompanyId = sessionUser.company_id;
  const sessionOrgCode = sessionUser.org_code;
  const sessionBusinessName = sessionUser.business_name;

  /* -------------------------------------------------
     ALLOWED ROLES
  ------------------------------------------------- */
  useEffect(() => {
    setAllowedFromRoles(["Retailer"]);
    setAllowedDestRoles(["Distributor", "Company"]);
  }, []);

  /* -------------------------------------------------
     DEFAULT SELECTIONS
  ------------------------------------------------- */
  useEffect(() => {
    setFromType("Retailer");
    setDestRole("Distributor");
  }, []);

  /* -------------------------------------------------
     FETCH ENTITIES
  ------------------------------------------------- */
  const fetchEntitiesByRole = async (role, setter) => {
    if (!role) return setter([]);

    // 🔹 FROM → Retailers under distributor
    if (role === "Retailer") {
      try {
        const res = await axios.get(
          `https://goelectronix.in/api/v1/org_user/search?dist_id=${sessionId}&login_id=${sessionId}`
        );

        const data = res.data || [];

        setter(
          data
            .filter((u) => Number(u.role) === 5)
            .map((u) => ({
              id: u.id,
              name: u.business_name || u.name,
              org_code: u.org_code,
              label: `${u.org_code} - ${u.business_name}`,
            }))
        );
      } catch (err) {
        console.error("Retailer fetch failed:", err);
        setter([]);
      }
      return;
    }

    // 🔹 DESTINATION → Distributor = session user
    if (role === "Distributor") {
      setter([
        {
          id: sessionUser.id,
          name: sessionUser.business_name,
          org_code: sessionUser.org_code,
          label: `${sessionUser.org_code} - ${sessionUser.business_name}`,
        },
      ]);
    }
  };

  /* -------------------------------------------------
     LOAD FROM ENTITIES
  ------------------------------------------------- */
  useEffect(() => {
    if (!fromType) return;
    setLoadingFrom(true);
    fetchEntitiesByRole(fromType, setFromEntities).finally(() =>
      setLoadingFrom(false)
    );
  }, [fromType]);

  /* -------------------------------------------------
     LOAD DEST ENTITIES
  ------------------------------------------------- */
  useEffect(() => {
    if (!destRole) return;
    setLoadingDest(true);
    fetchEntitiesByRole(destRole, setDestEntities).finally(() =>
      setLoadingDest(false)
    );
  }, [destRole]);

  /* -------------------------------------------------
     AUTO SELECT DEST (SESSION DISTRIBUTOR)
  ------------------------------------------------- */
  useEffect(() => {
    if (destRole === "Distributor") {
      setDestId({
        id: sessionUser.id,
        name: sessionUser.business_name,
        org_code: sessionUser.org_code,
        label: `${sessionUser.org_code} - ${sessionUser.business_name}`,
      });
    }
  }, [destRole]);

  /* -------------------------------------------------
     FETCH PACKAGES
  ------------------------------------------------- */
  useEffect(() => {
    if (!fromId?.id) {
      setPackageList([]);
      setSelectedPackage("");
      return;
    }

    setLoadingPackages(true);
    axios
      .get(
        `https://goelectronix.in/api/v1/keys/all-my-package-list?receiver_id=${fromId.id}`
      )
      .then((res) => setPackageList(res.data?.data || []))
      .catch(() => setPackageList([]))
      .finally(() => setLoadingPackages(false));
  }, [fromId]);

  /* -------------------------------------------------
     SUBMIT (YOUR PAYLOAD)
  ------------------------------------------------- */
  const handleSubmit = async () => {
    if (!fromId || !selectedPackage || !startKey || !destId)
      return Swal.fire("Missing Data", "Please complete all fields", "info");

    const pkg = packageList.find((p) => p.id === selectedPackage);
    if (!pkg) return;

    const qty = Number(startKey);
    const amount = Number(pkg.key_rate) * qty;

    const cnf_id = null;
    const ss_id = null;
    const dist_id = destId.id;

    const receiver_id = destId.id;
    const receiver_code = destId.org_code;
    const receiver_name = destId.name;
    const receiver_role = 4;

    const company_id = sessionCompanyId;

    const payload = {
      amount,
      cnf_id,
      ss_id,
      dist_id,
      company_id,
      created_by_code: sessionOrgCode,
      created_by_id: sessionId,
      created_by_name: sessionBusinessName,
      created_by_role: sessionRole,
      key_rate: pkg.key_rate,
      key_type: pkg.key_type,
      mode: 2,
      package_name: pkg.package_name,
      product_id: pkg.product_id,
      qty,
      receiver_code,
      receiver_id,
      receiver_name,
      role: receiver_role,
      sender_code: fromId.org_code,
      sender_id: fromId.id,
      sender_name: fromId.name,
      sender_package_id: pkg.id,
      user_package_id: pkg.user_package_id,
    };

    try {
      await axios.post(
        "https://goelectronix.in/api/v1/keys/assign",
        payload
      );

      Swal.fire("Success", "Keys pulled back successfully", "success");

      setFromId("");
      setSelectedPackage("");
      setStartKey("");
      setDestId("");
      setPackageList([]);
    } catch (err) {
      console.error("Assign failed:", err);
      Swal.fire("Error", "Operation failed", "error");
    }
  };

  /* -------------------------------------------------
     UI (UNCHANGED)
  ------------------------------------------------- */
  return (
    <Card title="Key Pull Back (with Re-assignment)">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
        <select
          value={fromType}
          onChange={(e) => setFromType(e.target.value)}
          className="rounded-lg border px-3 py-2 text-sm h-10"
        >
          <option value="">Select Type</option>
          {allowedFromRoles.map((r) => (
            <option key={r}>{r}</option>
          ))}
        </select>

        <div className="sm:col-span-2">
          {loadingFrom ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <EntitySearchSelect
              options={fromEntities}
              value={fromId}
              onChange={setFromId}
              placeholder="Search Retailer…"
              isDisabled={!fromType}
            />
          )}
        </div>

        <div className="sm:col-span-2 flex flex-col gap-2">
          <select
            value={selectedPackage}
            onChange={(e) => setSelectedPackage(Number(e.target.value))}
            disabled={!fromId}
            className="rounded-lg border px-3 py-2 text-sm"
          >
            <option value="">Select Package</option>
            {packageList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.package_name} - {p.balance_keys}
              </option>
            ))}
          </select>

          <input
            type="number"
            value={startKey}
            onChange={(e) => setStartKey(e.target.value)}
            disabled={!selectedPackage}
            placeholder="Enter key count"
            className="rounded-lg border px-3 py-2 text-sm"
          />
        </div>

        <div className="sm:col-span-2">
          {loadingDest ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <EntitySearchSelect
              options={destEntities}
              value={destId}
              onChange={setDestId}
              placeholder="Destination Distributor…"
            />
          )}
        </div>

        <div className="sm:col-span-6 flex justify-end gap-2">
          <RedButton onClick={() => window.location.reload()}>
            Reset
          </RedButton>
          <BlueButton onClick={handleSubmit}>
            Pull Back & Assign
          </BlueButton>
        </div>
      </div>
    </Card>
  );
}



// import { useEffect, useState } from "react";
// import axios from "axios";
// import Swal from "sweetalert2";
// import Card from "../components/Card";
// import RedButton from "../components/RedButton";
// import BlueButton from "../components/BlueButton";
// import EntitySearchSelect from "../components/EntitySearchSelect";

// export default function PullBackFlow() {
//   const [fromType, setFromType] = useState("");
//   const [fromId, setFromId] = useState("");
//   const [packageList, setPackageList] = useState([]);
//   const [selectedPackage, setSelectedPackage] = useState("");

//   const [startKey, setStartKey] = useState("");
//   const [reason, setReason] = useState("");
//   const [destRole, setDestRole] = useState("");
//   const [destId, setDestId] = useState("");

//   const [allowedFromRoles, setAllowedFromRoles] = useState([]);
//   const [allowedDestRoles, setAllowedDestRoles] = useState([]);

//   const [fromEntities, setFromEntities] = useState([]);
//   const [destEntities, setDestEntities] = useState([]);

//   const [loadingFrom, setLoadingFrom] = useState(false);
//   const [loadingDest, setLoadingDest] = useState(false);
//   const [loadingPackages, setLoadingPackages] = useState(false);

//   // 🔐 SESSION USER (CORRECT)
//   const authUserString = sessionStorage.getItem("authUserData");
//   const sessionUser = JSON.parse(authUserString);

//   const sessionUserId = sessionUser?.id; // 12570
//   const sessionCompanyId = sessionUser?.company_id;

//   /* ----------------------------------
//      ALLOWED ROLES
//   ---------------------------------- */
//   useEffect(() => {
//     setAllowedFromRoles(["Retailer"]);
//     setAllowedDestRoles(["Distributor", "Company"]);
//   }, []);

//   /* ----------------------------------
//      DEFAULT SELECTIONS
//   ---------------------------------- */
//   useEffect(() => {
//     setFromType("Retailer");
//     setDestRole("Distributor");
//   }, []);

//   /* ----------------------------------
//      FETCH ENTITIES (CORE FIX)
//   ---------------------------------- */
//   const fetchEntitiesByRole = async (role, setter) => {
//     if (!role) {
//       setter([]);
//       return;
//     }

//     // 🔹 FROM → Retailers under logged-in Distributor
//     if (role === "Retailer") {
//       try {
//         const res = await axios.get(
//           `https://goelectronix.in/api/v1/org_user/search?dist_id=${sessionUserId}&login_id=${sessionUserId}`
//         );

//         const data = res.data || [];

//         setter(
//           data
//             .filter((u) => Number(u.role) === 5)
//             .map((u) => ({
//               id: u.id,
//               name: u.business_name || u.retailer_name || u.name,
//               org_code: u.org_code,
//               label: `${u.org_code} - ${u.business_name}`,
//             }))
//         );
//       } catch (err) {
//         console.error("Retailer fetch failed:", err);
//         setter([]);
//       }
//       return;
//     }

//     // 🔹 DESTINATION → Distributor = session user
//     if (role === "Distributor") {
//       setter([
//         {
//           id: sessionUser.id,
//           name: sessionUser.business_name,
//           org_code: sessionUser.org_code,
//           label: `${sessionUser.org_code} - ${sessionUser.business_name}`,
//         },
//       ]);
//       return;
//     }

//     setter([]);
//   };

//   /* ----------------------------------
//      FROM ENTITIES
//   ---------------------------------- */
//   useEffect(() => {
//     if (!fromType) return;
//     setLoadingFrom(true);
//     fetchEntitiesByRole(fromType, setFromEntities).finally(() =>
//       setLoadingFrom(false)
//     );
//   }, [fromType]);

//   /* ----------------------------------
//      DEST ENTITIES
//   ---------------------------------- */
//   useEffect(() => {
//     if (!destRole) return;
//     setLoadingDest(true);
//     fetchEntitiesByRole(destRole, setDestEntities).finally(() =>
//       setLoadingDest(false)
//     );
//   }, [destRole]);

//   /* ----------------------------------
//      AUTO-SELECT DESTINATION (SESSION USER)
//   ---------------------------------- */
//   useEffect(() => {
//     if (destRole === "Distributor" && sessionUser) {
//       setDestId({
//         id: sessionUser.id,
//         name: sessionUser.business_name,
//         org_code: sessionUser.org_code,
//         label: `${sessionUser.org_code} - ${sessionUser.business_name}`,
//       });
//     }
//   }, [destRole, sessionUser]);

//   /* ----------------------------------
//      FETCH PACKAGES
//   ---------------------------------- */
//   useEffect(() => {
//     if (!fromId?.id) {
//       setPackageList([]);
//       setSelectedPackage("");
//       return;
//     }

//     setLoadingPackages(true);
//     axios
//       .get(
//         `https://goelectronix.in/api/v1/keys/all-my-package-list?receiver_id=${fromId.id}`
//       )
//       .then((res) => setPackageList(res.data?.data || []))
//       .catch(() => setPackageList([]))
//       .finally(() => setLoadingPackages(false));
//   }, [fromId]);

//   /* ----------------------------------
//      SUBMIT
//   ---------------------------------- */
//   const handleSubmit = async () => {
//     if (!fromId || !selectedPackage || !startKey || !destId)
//       return Swal.fire("Missing Data", "Please complete all fields", "info");

//     const pkg = packageList.find((p) => p.id === selectedPackage);
//     if (!pkg) return;

//     const payload = {
//       sender_id: fromId.id,
//       receiver_id: destId.id,
//       qty: Number(startKey),
//       key_type: pkg.key_type,
//       package_name: pkg.package_name,
//       key_rate: pkg.key_rate,
//       user_package_id: pkg.user_package_id,
//       sender_package_id: pkg.id,
//       mode: 2,
//     };

//     try {
//       await axios.post(
//         "https://goelectronix.in/api/v1/keys/assign",
//         payload
//       );
//       Swal.fire("Success", "Keys pulled back successfully", "success");

//       setFromId("");
//       setSelectedPackage("");
//       setStartKey("");
//       setDestId("");
//       setPackageList([]);
//     } catch {
//       Swal.fire("Error", "Operation failed", "error");
//     }
//   };

//   /* ----------------------------------
//      UI (UNCHANGED)
//   ---------------------------------- */
//   return (
//     <Card title="Key Pull Back (with Re-assignment)">
//       <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
//         {/* FROM TYPE */}
//         <select
//           value={fromType}
//           onChange={(e) => setFromType(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm h-10"
//         >
//           <option value="">Select Type</option>
//           {allowedFromRoles.map((r) => (
//             <option key={r}>{r}</option>
//           ))}
//         </select>

//         {/* FROM ENTITY */}
//         <div className="sm:col-span-2">
//           {loadingFrom ? (
//             <div className="text-sm text-slate-500">Loading…</div>
//           ) : (
//             <EntitySearchSelect
//               options={fromEntities}
//               value={fromId}
//               onChange={setFromId}
//               placeholder="Search Retailer…"
//               isDisabled={!fromType}
//             />
//           )}
//         </div>

//         {/* PACKAGE + QTY */}
//         <div className="sm:col-span-2 flex flex-col gap-2">
//           <select
//             value={selectedPackage}
//             onChange={(e) => setSelectedPackage(Number(e.target.value))}
//             disabled={!fromId}
//             className="rounded-lg border px-3 py-2 text-sm"
//           >
//             <option value="">Select Package</option>
//             {packageList.map((p) => (
//               <option key={p.id} value={p.id}>
//                 {p.package_name} - {p.qty}
//               </option>
//             ))}
//           </select>

//           <input
//             type="number"
//             value={startKey}
//             onChange={(e) => setStartKey(e.target.value)}
//             disabled={!selectedPackage}
//             placeholder="Enter key count"
//             className="rounded-lg border px-3 py-2 text-sm"
//           />
//         </div>

//         {/* DESTINATION */}
//         <div className="sm:col-span-2">
//           {loadingDest ? (
//             <div className="text-sm text-slate-500">Loading…</div>
//           ) : (
//             <EntitySearchSelect
//               options={destEntities}
//               value={destId}
//               onChange={setDestId}
//               placeholder="Destination Distributor…"
//             />
//           )}
//         </div>

//         {/* BUTTONS */}
//         <div className="sm:col-span-6 flex justify-end gap-2">
//           <RedButton onClick={() => window.location.reload()}>
//             Reset
//           </RedButton>
//           <BlueButton onClick={handleSubmit}>
//             Pull Back & Assign
//           </BlueButton>
//         </div>
//       </div>
//     </Card>
//   );
// }




// import { useEffect, useState } from "react";
// import axios from "axios";

// // import { fetchRoles } from "../Data/ROLES";
// import Swal from "sweetalert2";
// import Card from "../components/Card";
// import RedButton from "../components/RedButton";
// import BlueButton from "../components/BlueButton";
// import EntitySearchSelect from "../components/EntitySearchSelect";

// export default function PullBackFlow() {
//   const [rolesMap, setRolesMap] = useState({});
//   const [fromType, setFromType] = useState("");
//   const [fromId, setFromId] = useState("");
//   const [packageList, setPackageList] = useState([]); // ✅ new state
//   const [selectedPackage, setSelectedPackage] = useState(""); // ✅ selected package

//   const [startKey, setStartKey] = useState("");
//   const [endKey, setEndKey] = useState("");
//   const [reason, setReason] = useState("");
//   const [destRole, setDestRole] = useState("");
//   const [destId, setDestId] = useState("");

//   const [allowedFromRoles, setAllowedFromRoles] = useState([]);
//   const [allowedDestRoles, setAllowedDestRoles] = useState([]);

//   const [fromEntities, setFromEntities] = useState([]); // From entity list
//   const [destEntities, setDestEntities] = useState([]); // Destination entity list

//   const [loadingFrom, setLoadingFrom] = useState(false);
//   const [loadingDest, setLoadingDest] = useState(false);
//   const [loadingPackages, setLoadingPackages] = useState(false); // ✅ loading state for packages

//   // ✅ Load roles once
// //   useEffect(() => {
// //     (async () => {
// //       const roleData = await fetchRoles();
// //       setRolesMap(roleData);
// //       const allowed = getAllowedRolesBySession(roleData);
// //       setAllowedFromRoles(allowed);
// //       setAllowedDestRoles(allowed);
// //     })();
// //   }, []);

//   // ✅ Session-based allowed roles
// //   const getAllowedRolesBySession = (rolesMap) => {
// //     const authUserString = sessionStorage.getItem("authUserData");
// //     const authUser = JSON.parse(authUserString);
// //     const userRole = authUser?.data?.[0]?.role;

// //     const hierarchy = {
// //       2: ["Company", "CNF", "Super Stockist", "Distributor", "Retailer"],
// //       3: ["Company", "Super Stockist", "Distributor", "Retailer"],
// //       4: ["Company", "Distributor", "Retailer"],
// //       5: ["Company", "Retailer"],
// //     };

// //     return hierarchy[userRole] || [];
// //   };

//   // ✅ Common fetch function for roles (CNF, SS, Dist, Retailer)
//   const fetchEntitiesByRole = async (role, setter) => {
//     if (!role || role === "Company") return setter([]);

//     setter([]); // clear old
//     const apiUrl = "https://goelectronix.in/api/v1/org_user/search?company_id=5865&login_id=26";

//     const prefixMap = {
//       CNF: "CNF",
//       "Super Stockist": "SS",
//       Distributor: "DIST",
//       Retailer: "RTL",
//     };

//     const prefix = prefixMap[role];
//     if (!prefix) return setter([]);

//     try {
//       const res = await axios.get(apiUrl);
//       const data = res.data?.data || res.data || [];
//       const filtered = data
//         .filter((item) => item.org_code?.startsWith(prefix))
//         .map((item) => ({
//           id: item.id,
//           name: item.business_name || item.name || "Unnamed",
//           org_code: item.org_code,
//           label: `${item.org_code} - ${item.business_name || item.name}`,
//         }));
//       setter(filtered);
//     } catch (err) {
//       console.error(`❌ ${role} fetch failed:`, err);
//       setter([]);
//     }
//   };

//   // ✅ Fetch "From" entity list
//   useEffect(() => {
//     if (!fromType) return;
//     setLoadingFrom(true);
//     fetchEntitiesByRole(fromType, setFromEntities).finally(() => setLoadingFrom(false));
//   }, [fromType]);

//   // ✅ Fetch "Destination" entity list
//   useEffect(() => {
//     if (!destRole) return;
//     if (destRole === "Company") {
//       setDestEntities([]);
//       return;
//     }
//     setLoadingDest(true);
//     fetchEntitiesByRole(destRole, setDestEntities).finally(() => setLoadingDest(false));
//   }, [destRole]);

//   // ✅ Fetch package list for selected "From Entity"
//   useEffect(() => {
//   if (!fromId?.id) {
//     setPackageList([]);
//     setSelectedPackage("");
//     return;
//   }

//   const fetchPackages = async () => {
//     setLoadingPackages(true);
//     try {
//       const res = await axios.get(
//         `https://goelectronix.in/api/v1/keys/all-my-package-list?receiver_id=${fromId.id}`
//       );
//       const packages = res.data?.data || res.data || [];
//       setPackageList(packages);

//       // ✅ Don’t auto-select — let the user choose any package
//       // If you want to keep a previous selection if still valid:
//       // if (!packages.some(pkg => pkg.package_name === selectedPackage)) setSelectedPackage("");
//     } catch (err) {
//       console.error("❌ Package fetch failed:", err);
//       setPackageList([]);
//     } finally {
//       setLoadingPackages(false);
//     }
//   };

//   fetchPackages();
// }, [fromId]);


//   const canPickDestId = destRole !== "Company";

//   // ✅ Submit handler
//    const handleSubmit = async () => {
//   const authUserString = sessionStorage.getItem("authUserData");
//   const authUser = JSON.parse(authUserString)?.data?.[0];
//   if (!authUser)
//     return Swal.fire("Session Expired", "Please login again.", "warning");

//   const sessionRole = Number(authUser.role);
//   const sessionId = authUser.id;
//   const sessionCompanyId = authUser.company_id;
//   const sessionOrgCode = authUser.org_code;
//   const sessionBusinessName = authUser.business_name;
//   const sessionRoleName = rolesMap[sessionRole] || "Unknown";

//   if (!fromId)
//     return Swal.fire("Missing Field", "Select 'From Entity'.", "info");
//   if (!selectedPackage)
//     return Swal.fire("Missing Field", "Select a package.", "info");
//   if (!startKey)
//     return Swal.fire("Missing Field", "Enter number of keys to pull back.", "info");
//   if (canPickDestId && !destId)
//     return Swal.fire("Missing Field", "Select a destination entity.", "info");

//   const pkg = packageList.find((p) => p.id === selectedPackage);
//   if (!pkg)
//     return Swal.fire("Invalid Package", "Please select a valid package.", "error");

//   const qty = Number(startKey);
//   const amount = Number(pkg.key_rate) * qty;

//   // --- ✅ Role Mapping Logic ---
//   const getRoleId = (targetRoleNum) => {
//     if (sessionRole === targetRoleNum) return sessionId;

//     const destRoleNum = rolesMapReverse(destRole, rolesMap); // convert "Distributor" → 5
//     if (destRoleNum === targetRoleNum) return destId?.id;

//     return null;
//   };

//   const cnf_id = getRoleId(3);
//   const ss_id = getRoleId(4);
//   const dist_id = getRoleId(5);

//   const receiverIsCompany = destRole === "Company";
//   const receiver_id = receiverIsCompany ? sessionId : destId?.id;
//   const receiver_code = receiverIsCompany ? sessionOrgCode : destId?.org_code;
//   const receiver_name = receiverIsCompany ? sessionBusinessName : destId?.name;
//   const receiver_role = receiverIsCompany
//     ? sessionRole
//     : rolesMapReverse(destRole, rolesMap);

//   const company_id = receiverIsCompany ? sessionId : sessionCompanyId;

//   const payload = {
//     amount,
//     cnf_id,
//     ss_id,
//     dist_id,
//     company_id,
//     created_by_code: sessionOrgCode,
//     created_by_id: sessionId,
//     created_by_name: sessionBusinessName,
//     created_by_role: sessionRole,
//     key_rate: pkg.key_rate,
//     key_type: pkg.key_type,
//     mode: 2,
//     package_name: pkg.package_name,
//     product_id: pkg.product_id,
//     qty,
//     receiver_code,
//     receiver_id,
//     receiver_name,
//     role: receiver_role,
//     sender_code: fromId.org_code,
//     sender_id: fromId.id,
//     sender_name: fromId.name,
//     sender_package_id: pkg.id,
//     user_package_id: pkg.user_package_id,
//   };

//   console.log("🟢 Pullback Payload:", payload);

//   try {
//     const res = await axios.post("https://goelectronix.in/api/v1/keys/assign", payload);

//     if (res) {
//       await Swal.fire({
//         icon: "success",
//         title: "Success!",
//         text: "Pullback and reassignment successful!",
//         confirmButtonColor: "#3085d6",
//       });
//       // optional reset
//       setFromType("");
//       setFromId("");
//       setSelectedPackage("");
//       setStartKey("");
//       setDestRole("");
//       setDestId("");
//       setReason("");
//       setPackageList([]);
//     } else {
//       Swal.fire("Failed", res.data?.message || "Unknown error", "error");
//     }
//   } catch (err) {
//     console.error("❌ API Error:", err);
//     Swal.fire("Error", "Something went wrong while assigning keys.", "error");
//   }
// };


//   // ✅ helper to find role number by name
//   const rolesMapReverse = (roleName, map) => {
//     const found = Object.entries(map).find(([num, name]) => name === roleName);
//     return found ? Number(found[0]) : null;
//   };


//   return (
//     <Card  title="Key Pull Back (with Re-assignment)">
//       <div className="grid grid-cols-1 gap-3 sm:grid-cols-6">
//         {/* ✅ FROM TYPE */}
//         <select
//           value={fromType}
//           onChange={(e) => setFromType(e.target.value)}
//           className="rounded-lg border px-3 py-2 text-sm h-10"
//         >
//           <option value="">Select Type</option>
//           {allowedFromRoles
//             .filter((role) => role !== "Company")
//             .map((role) => (
//               <option key={role} value={role}>
//                 {role}
//               </option>
//             ))}
//         </select>
//         {/* ✅ FROM ENTITY */}
//         <div className="sm:col-span-2">
//           {loadingFrom ? (
//             <div className="text-sm text-slate-500">Loading {fromType} list…</div>
//           ) : (
//             <EntitySearchSelect
//               options={fromEntities}
//               value={fromId}
//               onChange={setFromId}
//               placeholder={`Search ${fromType || "Entity"}…`}
//               isDisabled={!fromType} // disable until fromType selected
//             />
//           )}
//         </div>
//         {/* ✅ PACKAGE SELECTOR + KEY INPUT */}
//         <div className="sm:col-span-2 flex flex-col gap-2">

//           {/* Package Dropdown */}
//           <select
//             value={selectedPackage}
//             onChange={(e) => {
//               setSelectedPackage(Number(e.target.value));
//               setStartKey("");
//             }}
//             disabled={!fromId}
//             className="rounded-lg border px-3 py-2 text-sm w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
//           >
//             <option value="">Select Package</option>
//             {loadingPackages ? (
//               <option disabled>Loading packages…</option>
//             ) : packageList.length === 0 ? (
//               <option disabled>No packages found</option>
//             ) : (
//               packageList.map((pkg, i) => (
//                 <option key={i} value={pkg.id}>
//                   {pkg.package_name} - {pkg.qty}
//                 </option>
//               ))
//             )}
//           </select>

//           {/* Key Input */}
//           <input
//             type="number"
//             value={startKey}
//             min={1}
//             disabled={!selectedPackage}
//             onChange={(e) => {
//               const val = e.target.value;
//               const selectedPkg = packageList.find(pkg => pkg.id === selectedPackage);
//               const maxQty = selectedPkg?.qty || Infinity;

//               if (/^\d*$/.test(val) && Number(val) <= maxQty) {
//                 setStartKey(val);
//               }
//             }}
//             placeholder="Enter Key to pullback"
//             className="rounded-lg border px-3 py-2 text-sm w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
//           />

//           {selectedPackage && (
//             <p className="text-xs text-slate-500">
//               Max allowed:{" "}
//               <span className="font-medium text-slate-700">
//                 {packageList.find((pkg) => pkg.id === selectedPackage)?.qty || 0}
//               </span>
//             </p>
//           )}
//         </div>
//         {/* ✅ DESTINATION SECTION */}
//         <div className="sm:col-span-6 grid grid-cols-6 gap-3 mt-3">

//           {/* Destination Role */}
//           <div className="sm:col-span-2">
//             <select
//               value={destRole}
//               onChange={(e) => {
//                 setDestRole(e.target.value);
//                 setDestId("");
//               }}
//               disabled={!startKey}
//               className="rounded-lg border px-3 py-2 text-sm h-10 w-full disabled:bg-gray-100 disabled:cursor-not-allowed"
//             >
//               <option value="">Select Destination</option>
//               {allowedDestRoles.map((role) => (
//                 <option key={role} value={role}>
//                   {role}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Destination Entity */}
//           <div className="sm:col-span-2">
//             {canPickDestId ? (
//               loadingDest ? (
//                 <div className="text-sm text-slate-500">Loading {destRole} list…</div>
//               ) : (
//                 <EntitySearchSelect
//                   options={destEntities.filter(
//                     (entity) => entity.id !== fromId?.id
//                   )}
//                   value={destId}
//                   onChange={(selected) => {
//                     if (selected?.id === fromId?.id) {
//                       alert("You cannot select the same user as both source and destination.");
//                       setDestId("");
//                     } else {
//                       setDestId(selected);
//                     }
//                   }}
//                   placeholder={`Search ${destRole || "Entity"}…`}
//                   isDisabled={!destRole}
//                 />
//               )
//             ) : (
//               <div className="flex items-center text-sm text-slate-600">
//                 Assigning back to <b className="ml-1">Company</b>
//               </div>
//             )}
//           </div>

//           {/* ✅ Reason (Read-only) */}
//           <div className="sm:col-span-2">
//             <input
//               value={
//                 startKey && fromId && (destId || destRole === "Company")
//                   ? `${startKey} key${Number(startKey) > 1 ? "s" : ""} will be pulled back from ${
//                       fromId.name || fromId.label || "Selected User"
//                     } to ${
//                       destRole === "Company"
//                         ? "Company"
//                         : destId?.name || destId?.label || "Selected Destination"
//                     }`
//                   : reason
//               }
//               onChange={(e) => setReason(e.target.value)}
//               placeholder="Reason or summary (optional)"
//               className="rounded-lg border px-3 py-2 text-sm w-full"
//               readOnly // 👈 always read-only
//             />
//           </div>

//         </div>
//         {/* ✅ BUTTONS */}
//         <div className="sm:col-span-6 flex items-center justify-end gap-2">
//           <RedButton
            
//             onClick={() => {
//               setFromType("");
//               setFromId("");
//               setSelectedPackage("");
//               setStartKey("");
//               setDestRole("");
//               setDestId("");
//               setReason("");
//               setPackageList([]);
//             }}
//           >
//             Reset
//           </RedButton>
//           <BlueButton
//             onClick={handleSubmit}
//             disabled={!destId && destRole !== "Company"}
//           >
//             Pull Back & Assign
//           </BlueButton>
//         </div>
//       </div>
//     </Card>
//   );
// }